/**
 * Configuration for settings routes with custom layout behavior.
 *
 * Routes are organized by domain/feature area for better maintainability.
 * Each route can specify metadata like `hideSettingsSidebar` or `hideTrialModal`
 * to control layout rendering behavior.
 *
 * NOTE: The `fullPath` array should contain only static path segments.
 * Dynamic parameters (like :roleId, :userId, etc.) should be EXCLUDED.
 *
 * Example:
 * - Route path: /settings/users/roles-and-permissions/:roleId/edit
 * - fullPath: ['settings', 'users', 'roles-and-permissions', 'edit']
 *   (notice :roleId is omitted)
 */

// General settings routes
const GENERAL_SETTINGS_ROUTES = {
  SETTINGS_INDEX: {
    path: 'settings',
    fullPath: ['settings'],
    hideSettingsSidebar: false,
  },
  ACCOUNT: {
    path: 'account',
    fullPath: ['settings', 'account'],
    hideSettingsSidebar: false,
  },
  LEGACY: {
    path: 'legacy',
    fullPath: ['settings', 'legacy'],
    hideSettingsSidebar: false,
  },
  SEATS_INVITE: {
    path: 'seats/invite',
    fullPath: ['settings', 'seats', 'invite'],
    hideSettingsSidebar: false,
  },
  CONNECT_API: {
    path: 'connect-api',
    fullPath: ['settings', 'connect-api'],
    hideSettingsSidebar: false,
  },
  OPTIONAL_FEATURES: {
    path: 'optional-features',
    fullPath: ['settings', 'optional-features'],
    hideSettingsSidebar: false,
  },
  EMAIL_TEMPLATES: {
    path: 'email-templates',
    fullPath: ['settings', 'email-templates'],
    hideSettingsSidebar: false,
  },
} as const;

// SSO-related settings routes (nested under /settings/sso)
const SSO_SETTINGS_ROUTES = {
  SSO: {
    path: 'sso',
    fullPath: ['settings', 'sso'],
    hideSettingsSidebar: false,
  },

  SSO_CONFIGURATION: {
    path: 'configuration',
    fullPath: ['settings', 'sso', 'configuration'],
    hideSettingsSidebar: false,
  },
  SSO_CONFIGURATION_INDEX: {
    path: '', // index route
    fullPath: ['settings', 'sso', 'configuration'],
    hideSettingsSidebar: false,
  },
  SSO_CONFIGURATION_PROVIDER: {
    path: ':providerId',
    fullPath: ['settings', 'sso', 'configuration'],
    hideSettingsSidebar: false,
  },
} as const;

// Workspace settings routes (nested under /settings/workspaces)
const WORKSPACE_SETTINGS_ROUTES = {
  WORKSPACES: {
    path: 'workspaces',
    fullPath: ['settings', 'workspaces'],
    hideSettingsSidebar: false,
  },
} as const;

// User management settings routes (nested under /settings/users)
const USER_SETTINGS_ROUTES = {
  USERS: {
    path: 'users',
    fullPath: ['settings', 'users'],
    hideSettingsSidebar: false,
  },
  USER_MANAGEMENT: {
    path: 'user-management',
    fullPath: ['settings', 'users', 'user-management'],
    hideSettingsSidebar: false,
  },
  ROLES_AND_PERMISSIONS: {
    path: 'roles-and-permissions',
    fullPath: ['settings', 'users', 'roles-and-permissions'],
    hideSettingsSidebar: false,
  },
  EDIT_ROLE: {
    path: ':roleId/edit',
    // Only static segments - dynamic :roleId is excluded from fullPath
    fullPath: ['settings', 'users', 'roles-and-permissions', 'edit'],
    hideSettingsSidebar: true,
  },
  USER_GROUPS: {
    path: 'user-groups',
    fullPath: ['settings', 'users', 'user-groups'],
    hideSettingsSidebar: false,
  },
} as const;

// Custom fields settings routes
const CUSTOM_FIELDS_SETTINGS_ROUTES = {
  CUSTOM_FIELDS: {
    path: 'custom-fields',
    fullPath: ['settings', 'custom-fields'],
    hideSettingsSidebar: false,
  },
} as const;

// Billing settings routes
const BILLING_SETTINGS_ROUTES = {
  SETTINGS_BILLING: {
    path: 'billing/:planName',
    fullPath: ['settings', 'billing'],
    hideTrialModal: true,
    allowSuspendedAccess: true,
  },
  SETTINGS_PLAN: {
    path: 'plan',
    fullPath: ['settings', 'plan'],
    hideTrialModal: true,
    allowSuspendedAccess: true,
  },
  SETTINGS_SUMMARY: {
    path: 'summary',
    fullPath: ['settings', 'summary'],
    allowSuspendedAccess: true,
  },
} as const;

export const SETTINGS_ROUTES = {
  ...GENERAL_SETTINGS_ROUTES,
  ...SSO_SETTINGS_ROUTES,
  ...WORKSPACE_SETTINGS_ROUTES,
  ...USER_SETTINGS_ROUTES,
  ...CUSTOM_FIELDS_SETTINGS_ROUTES,
  ...BILLING_SETTINGS_ROUTES,
} as const;
